import Link from 'next/link';
import type { Form } from '../../payload-types';
import { cn } from '../../utilities/classname';
import { isString } from '../../utilities/is-something';
import { pathForEntity } from '../../utilities/markdown-refs/path-for-entity';

/**
 * Public path of the privacy policy an editor linked to a form, or null when
 * there is nothing to link.
 *
 * Every tenant render path reads with `overrideAccess: false`, so a policy the
 * public cannot read (a draft, an unpublished one, another tenant's) stays an
 * unpopulated id string, and a trashed one comes back null. Neither has a page
 * to link, the same rule the nav's policy-link view applies.
 */
export function resolveFormPrivacyPolicyHref(policy: Form['privacyPolicy']): string | null {
  if (!policy || isString(policy)) return null;
  return pathForEntity('policies', policy.slug ?? '');
}

/**
 * One line under a form linking the privacy policy set on it. Renders nothing
 * unless an editor chose a published policy, so no site gains copy it did not
 * ask for.
 *
 * `grow-0` because daisyUI's `.card-body :where(p)` sets `flex-grow: 1`, which
 * stretches the line in a flex-column form inside a card.
 */
export const FormPrivacyPolicyLink: React.FC<{
  policy: Form['privacyPolicy'];
  className?: string;
}> = ({ policy, className }) => {
  const href = resolveFormPrivacyPolicyHref(policy);
  if (!href) return null;

  return (
    <p className={cn('grow-0 text-xs', className)}>
      See our{' '}
      <Link href={href} prefetch={false} className="link link-hover">
        privacy policy
      </Link>
      {' '}for how we use your details.
    </p>
  );
};
